import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Modal } from 'src/components/Modal/Modal';
import { Button } from 'src/components/Button';
import { PermissionGuard } from 'src/components/PermissionGuard';
import { convertDate, formatFieldString, shortenString } from 'src/components/Table/helpers';
import { formatCaseUserName } from 'src/api/adminCompliance';
import { formatKycStatusLabel } from 'src/api/adminUsers';
import { formatBigNumber } from 'src/utils/constants';
import { PERMISSIONS } from 'src/utils/permissions';
import { ROUTE_USERS } from 'src/utils/routes';

import {
  ActionRow,
  Breadcrumb,
  Card,
  CardTitle,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  HeadlineRow,
  MetaLine,
  StatusBadge,
  Title,
  TitleGroup,
  UserLink,
} from 'src/pages/Transactions/styled';
import CopyableValue from 'src/pages/Transactions/CopyableValue';

interface OrderDetailsModalProps {
  order: any;
  onConfirm: () => void;
  onCancel: () => void;
  onClose: () => void;
}

const SectionSpacer = () => <div style={{ height: '1rem' }} />;

const Screenshot = styled.img`
  width: 100%;
  max-width: 300px;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.grey};
  margin-top: 0.25rem;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

export { Screenshot };


const formatAmount = (value: unknown, prefix = '') => {
  if (value == null || value === '') return null;
  try {
    return `${prefix}${formatBigNumber(value as any)}`;
  } catch {
    return `${prefix}${value}`;
  }
};

const isPendingApproval = (status?: string) => {
  const s = String(status || '').toLowerCase();
  return s === 'in_progress' || s === 'approval pending';
};

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  order,
  onConfirm,
  onCancel,
  onClose,
}) => {
  const navigate = useNavigate();
  const user = order.user_id;
  const userId = user?.id || user?._id;
  const userName = formatCaseUserName(
    user ? { name: user.name, email: user.email } : null,
  );
  const userEmail = user?.email as string | undefined;
  const status = order.status ? String(order.status) : '';
  const orderNumber = order.order_number != null ? String(order.order_number) : '';
  const createdAt = order.created_at as string | undefined;

  const headline = orderNumber
    ? `Order #${orderNumber}`
    : [order.payment_coin, order.blockchain].filter(Boolean).join(' · ') || 'Pre-sale order';

  const createdMeta = createdAt
    ? `Created ${convertDate(createdAt).date}${
        convertDate(createdAt).time ? ` · ${convertDate(createdAt).time}` : ''
      }`
    : null;

  return (
    <Modal isOpen onClose={onClose}>
      <Breadcrumb>
        Pre-sale orders{orderNumber ? ` / #${orderNumber}` : ''}
      </Breadcrumb>

      <HeadlineRow>
        <TitleGroup>
          <Title>
            {headline}
            {status ? (
              <StatusBadge $status={status}>{formatFieldString(status)}</StatusBadge>
            ) : null}
          </Title>
          {createdMeta ? <MetaLine>{createdMeta}</MetaLine> : null}
        </TitleGroup>
      </HeadlineRow>

      {(userName || userId || userEmail) && (
        <>
          <Card>
            <CardTitle>User</CardTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Name</DetailLabel>
                {userId ? (
                  <DetailValue title={userEmail ? `${userName} (${userEmail})` : userName}>
                    <UserLink
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate(`${ROUTE_USERS}/${userId}`);
                      }}
                    >
                      {userName && userName !== '—' ? userName : shortenString(String(userId))}
                    </UserLink>
                  </DetailValue>
                ) : (
                  <DetailValue>{userName || '—'}</DetailValue>
                )}
              </DetailItem>
              <DetailItem>
                <DetailLabel>Email</DetailLabel>
                <DetailValue $muted={!userEmail}>{userEmail || '—'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>BxLend ID</DetailLabel>
                <DetailValue $muted={!user?.bxlend_id}>{user?.bxlend_id || '—'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>KYC status</DetailLabel>
                <DetailValue>
                  {user?.kyc_status ? (
                    <StatusBadge $status={String(user.kyc_status)}>
                      {formatKycStatusLabel(user.kyc_status)}
                    </StatusBadge>
                  ) : (
                    '—'
                  )}
                </DetailValue>
              </DetailItem>
            </DetailGrid>
          </Card>
          <SectionSpacer />
        </>
      )}

      <Card>
        <CardTitle>Order</CardTitle>
        <DetailGrid>
          {orderNumber ? (
            <DetailItem>
              <DetailLabel>Order number</DetailLabel>
              <DetailValue>{orderNumber}</DetailValue>
            </DetailItem>
          ) : null}
          <DetailItem>
            <DetailLabel>Status</DetailLabel>
            <DetailValue>
              {status ? (
                <StatusBadge $status={status}>{formatFieldString(status)}</StatusBadge>
              ) : (
                '—'
              )}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Presale stage</DetailLabel>
            <DetailValue $muted={order.presale_stage == null}>
              {order.presale_stage != null ? `Stage ${order.presale_stage}` : '—'}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Amount (USD)</DetailLabel>
            <DetailValue>{formatAmount(order.amount_in_usd, '$') || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Token allocation</DetailLabel>
            <DetailValue>
              {formatAmount(order.token_allocation)
                ? `${formatAmount(order.token_allocation)} BXT`
                : '—'}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>BXT base price</DetailLabel>
            <DetailValue>{formatAmount(order.bxlend_token_base_price, '$') || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Coin price</DetailLabel>
            <DetailValue>{formatAmount(order.coin_price, '$') || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Coin converted price</DetailLabel>
            <DetailValue>{formatAmount(order.coin_converted_price, '$') || '—'}</DetailValue>
          </DetailItem>
        </DetailGrid>
      </Card>

      <SectionSpacer />

      <Card>
        <CardTitle>Payment</CardTitle>
        <DetailGrid>
          <DetailItem>
            <DetailLabel>Blockchain</DetailLabel>
            <DetailValue $muted={!order.blockchain}>{order.blockchain || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Payment coin</DetailLabel>
            <DetailValue $muted={!order.payment_coin}>{order.payment_coin || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Deposit address</DetailLabel>
            {order.deposit_address ? (
              <CopyableValue value={String(order.deposit_address)} label="Deposit address" />
            ) : (
              <DetailValue $muted>—</DetailValue>
            )}
          </DetailItem>
          <DetailItem>
            <DetailLabel>Transaction ID</DetailLabel>
            {order.blockchain_transaction_id ? (
              <CopyableValue value={String(order.blockchain_transaction_id)} label="Transaction ID" />
            ) : (
              <DetailValue $muted>—</DetailValue>
            )}
          </DetailItem>
        </DetailGrid>
      </Card>

      {order.payment_screenshot ? (
        <>
          <SectionSpacer />
          <Card>
            <CardTitle>Payment proof</CardTitle>
            <Screenshot
              src={order.payment_screenshot}
              alt="Payment screenshot"
              onClick={() => window.open(order.payment_screenshot, '_blank')}
              loading="lazy"
            />
          </Card>
        </>
      ) : null}

      {isPendingApproval(status) && (
        <PermissionGuard permission={PERMISSIONS.TRANSACTION_EXECUTE}>
          <ActionRow>
            <Button text="Cancel order" onClick={onCancel} $fullWidth className="outline" />
            <Button text="Confirm order" className="black" onClick={onConfirm} $fullWidth />
          </ActionRow>
        </PermissionGuard>
      )}
    </Modal>
  );
};

export default OrderDetailsModal;
